/**
 * Dick's Potato Chips — Custom Theme JavaScript
 * 
 * Handles:
 * - Header glass-blur on scroll
 * - Mobile sticky bar visibility (PDP)
 * - Collapsible tabs (PDP)
 * - Mini-cart drawer open/close
 * - AJAX add-to-cart with cart:updated event
 * 
 * Dependencies: None — vanilla JS only.
 * 
 * Inputs: DOM elements with specific class names from dicks-custom.css
 * Outputs: CSS class toggles, fetch requests to Shopify Cart API
 * 
 * Known limitations:
 * - Mini-cart drawer uses Shopify Cart API (/cart.js) — response format
 *   varies by theme version. Tested against Dawn 4.0.
 * - Sticky bar IntersectionObserver may not fire on very fast scroll
 *   on older iOS devices.
 */

(function() {
  'use strict';

  /* -------------------------------------------------------
     1. HEADER — Glass-blur on scroll
     ------------------------------------------------------- */
  function initHeaderScroll() {
    const header = document.querySelector('.header-wrapper');
    if (!header) return;

    const SCROLL_THRESHOLD = 20;

    function onScroll() {
      if (window.scrollY > SCROLL_THRESHOLD) {
        header.classList.add('header--scrolled');
      } else {
        header.classList.remove('header--scrolled');
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // Set initial state
  }

  /* -------------------------------------------------------
     2. MOBILE STICKY BAR — Shows when Add to Bag scrolls out
     ------------------------------------------------------- */
  function initStickyBar() {
    const stickyBar = document.querySelector('.dicks-sticky-bar');
    const addToCartBtn = document.querySelector('.dicks-pdp-add-btn');
    
    if (!stickyBar || !addToCartBtn) return;

    // Only show the sticky bar on mobile (CSS handles display:none on desktop)
    const observer = new IntersectionObserver(
      function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            stickyBar.classList.remove('is-visible');
          } else {
            stickyBar.classList.add('is-visible');
          }
        });
      },
      { threshold: 0, rootMargin: '-56px 0px 0px 0px' }
    );

    observer.observe(addToCartBtn);
  }

  /* -------------------------------------------------------
     3. COLLAPSIBLE TABS — Accordion behavior
     ------------------------------------------------------- */
  function initCollapsibleTabs() {
    var tabs = document.querySelectorAll('.dicks-tab__trigger');
    
    // Set initial aria-expanded state on all triggers
    tabs.forEach(function(trigger) {
      var tab = trigger.closest('.dicks-tab');
      if (tab && !trigger.hasAttribute('aria-expanded')) {
        trigger.setAttribute('aria-expanded', tab.classList.contains('is-open') ? 'true' : 'false');
      }
    });

    tabs.forEach(function(trigger) {
      trigger.addEventListener('click', function() {
        var tab = trigger.closest('.dicks-tab');
        if (!tab) return;

        var content = tab.querySelector('.dicks-tab__content');
        var inner = tab.querySelector('.dicks-tab__content-inner');
        
        if (tab.classList.contains('is-open')) {
          // Close
          tab.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
          if (content) content.style.maxHeight = '0';
        } else {
          // Close siblings
          var siblings = tab.parentElement.querySelectorAll('.dicks-tab.is-open');
          siblings.forEach(function(sib) {
            sib.classList.remove('is-open');
            var sibTrigger = sib.querySelector('.dicks-tab__trigger');
            if (sibTrigger) sibTrigger.setAttribute('aria-expanded', 'false');
            var sibContent = sib.querySelector('.dicks-tab__content');
            if (sibContent) sibContent.style.maxHeight = '0';
          });

          // Open this one
          tab.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
          if (content && inner) {
            content.style.maxHeight = inner.scrollHeight + 'px';
          }
        }
      });
    });
  }

  /* -------------------------------------------------------
     4. CART NOTIFICATION (Dawn) — single site-wide cart UI

     The custom side drawer is retired. Every add-to-cart flow
     dispatches `cart:updated`; this opens Dawn's top
     cart-notification so there is ONE consistent cart UI.
     ------------------------------------------------------- */
  function initCartNotification() {
    document.addEventListener('cart:updated', function(e) {
      showCartNotification(e && e.detail);
    });
  }

  /**
   * Open Dawn's <cart-notification> for the freshly added item.
   *
   * Input: addResponse (optional) — the /cart/add.js JSON. When it carries
   *        `sections` + `key` (single-item adds we control) it renders
   *        directly. Otherwise we derive the most-recent cart line and fetch
   *        the notification sections (covers multi-item build-a-box adds).
   * Output: renders + opens the notification. No-op if the element is absent
   *        (e.g. cart type set to "page") — the add itself still succeeded.
   */
  function showCartNotification(addResponse) {
    var cn = document.querySelector('cart-notification');
    if (!cn) return;

    if (addResponse && addResponse.sections && addResponse.key) {
      cn.renderContents(addResponse);
      return;
    }

    var sectionIds = cartNotificationSections();
    fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        if (!cart.items || !cart.items.length) return;
        var key = cart.items[cart.items.length - 1].key;
        return fetch(window.location.pathname + '?sections=' + encodeURIComponent(sectionIds))
          .then(function(r) { return r.json(); })
          .then(function(sections) {
            cn.renderContents({ key: key, sections: sections });
          });
      })
      .catch(function(err) {
        console.error('[Dick\'s Cart] notification failed:', err.message);
      });
  }

  /* Comma-separated section ids Dawn's cart-notification needs from /cart/add.js. */
  function cartNotificationSections() {
    var cn = document.querySelector('cart-notification');
    return cn ? cn.getSectionsToRender().map(function(s) { return s.id; }).join(',') : '';
  }

  /* -------------------------------------------------------
     5. AJAX ADD-TO-CART
     ------------------------------------------------------- */
  function initAjaxCart() {
    document.addEventListener('submit', function(e) {
      var form = e.target;
      if (!form.classList.contains('dicks-ajax-cart-form')) return;

      e.preventDefault();

      var submitBtn = form.querySelector('[type="submit"]');
      var originalText = submitBtn ? submitBtn.textContent : '';

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Adding...';
      }

      var formData = new FormData(form);
      var sections = cartNotificationSections();
      if (sections) {
        formData.append('sections', sections);
        formData.append('sections_url', window.location.pathname);
      }

      fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      })
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Add to cart failed: ' + response.status);
        }
        return response.json();
      })
      .then(function(data) {
        // Open Dawn's cart-notification with the added item
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));

        if (submitBtn) {
          submitBtn.textContent = 'Added ✓';
          setTimeout(function() {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
          }, 1500);
        }
      })
      .catch(function(error) {
        console.error('[Dick\'s Cart] Add failed:', error.message);
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalText;
        }
      });
    });
  }

  /* -------------------------------------------------------
     6. BUNDLE VARIANT PILLS
     ------------------------------------------------------- */
  function initBundlePills() {
    var pillSelectors = document.querySelectorAll('.dicks-pill-selector');

    pillSelectors.forEach(function(selector) {
      var pills = selector.querySelectorAll('.dicks-pill:not(.dicks-pill--disabled)');
      var form = selector.closest('.dicks-bundle-card, .dicks-bundle-pdp');
      var hiddenInput = form ? form.querySelector('.dicks-variant-input') : null;
      var submitBtn = form ? form.querySelector('.dicks-bundle-submit') : null;
      var productImage = form ? form.querySelector('.dicks-bundle-image') : null;

      pills.forEach(function(pill) {
        pill.addEventListener('click', function() {
          // Deselect all
          pills.forEach(function(p) {
            p.classList.remove('dicks-pill--selected');
          });

          // Select this one
          pill.classList.add('dicks-pill--selected');

          // Update hidden variant input
          var variantId = pill.getAttribute('data-variant-id');
          if (hiddenInput && variantId) {
            hiddenInput.value = variantId;
          }

          // Enable submit button
          if (submitBtn) {
            submitBtn.disabled = false;
          }

          // Swap product image if variant has one
          var variantImage = pill.getAttribute('data-variant-image');
          if (productImage && variantImage) {
            productImage.src = variantImage;
          }
        });
      });
    });
  }

  /* -------------------------------------------------------
     INIT — Run on DOMContentLoaded
     ------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', function() {
    initHeaderScroll();
    initStickyBar();
    initCollapsibleTabs();
    initCartNotification();
    initAjaxCart();
    initBundlePills();
    // Dawn's native product form shows the top cart-notification on PDP add.
    // The custom side drawer, its cart-icon override, and the PDP form intercept
    // are all retired — cart-notification is the single cart UI site-wide.
  });

})();
